<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

import WorkScheduleForm from './WorkScheduleForm.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const showFormPopup = ref(false);
const showDeletePopup = ref(false);
const selectedSchedule = ref(null);

const records = computed(() => getters['workSchedules/getWorkSchedules'].value);
const uiFlags = computed(() => getters['workSchedules/getUIFlags'].value);

const tableHeaders = computed(() => [
  t('WORK_SCHEDULES.LIST.TABLE_HEADER.NAME'),
  t('WORK_SCHEDULES.LIST.TABLE_HEADER.GRID'),
  t('WORK_SCHEDULES.LIST.TABLE_HEADER.ACTION'),
]);

// "Seg–Sex 08:00–20:00, Sáb 08:00–12:00" instead of seven rows: consecutive days whose ranges are identical
// are collapsed, which is how a person describes a schedule out loud. Days off are simply left out.
const summarize = schedule => {
  const byDay = Array.from({ length: 7 }, (_, day) =>
    (schedule.shifts ?? [])
      .filter(shift => shift.day_of_week === day)
      .map(shift => `${shift.from}–${shift.to}`)
      .join(', ')
  );

  const groups = [];
  byDay.forEach((hours, day) => {
    if (!hours) return;
    const previous = groups[groups.length - 1];
    if (previous && previous.hours === hours && previous.lastDay === day - 1) {
      previous.lastDay = day;
      return;
    }
    groups.push({ firstDay: day, lastDay: day, hours });
  });

  return groups
    .map(({ firstDay, lastDay, hours }) => {
      const days =
        firstDay === lastDay
          ? t(`WORK_SCHEDULES.DAYS_SHORT.${firstDay}`)
          : `${t(`WORK_SCHEDULES.DAYS_SHORT.${firstDay}`)}–${t(`WORK_SCHEDULES.DAYS_SHORT.${lastDay}`)}`;
      return `${days} ${hours}`;
    })
    .join(' · ');
};

const openAdd = () => {
  selectedSchedule.value = null;
  showFormPopup.value = true;
};

const openEdit = schedule => {
  selectedSchedule.value = schedule;
  showFormPopup.value = true;
};

const closeForm = () => {
  showFormPopup.value = false;
  selectedSchedule.value = null;
};

const openDelete = schedule => {
  selectedSchedule.value = schedule;
  showDeletePopup.value = true;
};

const closeDelete = () => {
  showDeletePopup.value = false;
};

const confirmDeletion = async () => {
  const { id } = selectedSchedule.value;
  closeDelete();
  try {
    await store.dispatch('workSchedules/delete', id);
    useAlert(t('WORK_SCHEDULES.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('WORK_SCHEDULES.DELETE.API.ERROR_MESSAGE'));
  }
};

onBeforeMount(() => {
  store.dispatch('workSchedules/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('WORK_SCHEDULES.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="t('WORK_SCHEDULES.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('WORK_SCHEDULES.HEADER')"
        :description="t('WORK_SCHEDULES.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="t('WORK_SCHEDULES.HEADER_BTN_TXT')"
            size="sm"
            @click="openAdd"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="records"
        :no-data-message="t('WORK_SCHEDULES.LIST.404')"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="schedule in items"
            :key="schedule.id"
            :item="schedule"
          >
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12">
                  {{ schedule.name }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ summarize(schedule) }}
                </span>
              </BaseTableCell>
              <BaseTableCell align="end">
                <div class="flex flex-shrink-0 justify-end gap-3">
                  <Button
                    v-tooltip.top="t('WORK_SCHEDULES.FORM.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEdit(schedule)"
                  />
                  <Button
                    v-tooltip.top="t('WORK_SCHEDULES.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDelete(schedule)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showFormPopup" :on-close="closeForm">
      <WorkScheduleForm
        :key="selectedSchedule?.id ?? 'new'"
        :selected-schedule="selectedSchedule"
        @close="closeForm"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="t('WORK_SCHEDULES.DELETE.CONFIRM.TITLE')"
      :message="t('WORK_SCHEDULES.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="t('WORK_SCHEDULES.DELETE.CONFIRM.YES')"
      :reject-text="t('WORK_SCHEDULES.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
